import { useMemo } from 'react';
import Icon, { IconNames } from 'components/Icon/Icon';
import { Meeting, MeetingStatus } from 'types/Meeting';

interface EventStatusBadgeProps {
  event: {
    meeting?: Meeting;
    eventType: string;
  } | null;
  outlined?: boolean;
}

/**
 * Composant qui affiche un badge de statut pour un événement
 * Le style du badge change en fonction du type d'événement et de son statut
 */
const EventStatusBadge = ({ event, outlined = false }: EventStatusBadgeProps) => {
  const statusBadge = useMemo(() => {
    let textColor = 'text-primary';
    let border = '';
    let bgColor = 'bg-light';
    let iconName: IconNames = 'ClockIcon';
    let label = '?';

    if (event?.eventType === 'personal') {
      textColor = outlined ? 'text-stone-400' : 'text-stone-400';
      border = outlined ? 'border border-dark' : 'border border-stone-300';
      bgColor = outlined ? 'bg-white' : 'bg-stone-50';
      iconName = 'PersonalEventIcon';
      label = 'Evènement personnel';
    } else if (event?.eventType === 'availability') {
      textColor = outlined ? 'text-[#0284c7]' : 'text-[#0284c7]';
      border = outlined ? 'border border-dark' : 'border border-[#0284c7]';
      bgColor = outlined ? 'bg-white' : 'bg-[rgba(2,132,199,0.15)]';
      iconName = 'AvailabilityEventIcon';
      label = 'Disponibilité';
    } else {
      switch (event?.meeting?.status as MeetingStatus) {
        case 'cancelled':
          textColor = outlined ? 'text-stone-400' : 'text-stone-400';
          border = outlined ? 'border border-dark' : 'border border-stone-300';
          bgColor = outlined ? 'bg-white' : 'bg-stone-50';
          iconName = 'TrashIcon';
          label = 'Annulé';
          break;
        case 'accepted':
          textColor = outlined ? 'text-[#0284c7]' : 'text-white';
          border = outlined ? 'border border-[#0284c7]' : '';
          bgColor = outlined ? 'bg-white' : 'bg-[#0284c7]';
          iconName = 'CheckIcon';
          label = 'Accepté';
          break;
        case 'denied':
          textColor = outlined ? 'text-red-500' : 'text-red-500';
          border = outlined ? 'border border-red-500' : 'border border-red-500';
          bgColor = outlined ? 'bg-white' : 'bg-red-100';
          iconName = 'XMarkIcon';
          label = 'Refusé';
          break;
        case 'pending':
          textColor = outlined ? 'text-orange-600' : 'text-white';
          border = outlined ? 'border border-orange-500' : '';
          bgColor = outlined ? 'bg-white' : 'bg-orange-400';
          iconName = 'ClockIcon';
          label = 'En attente';
          break;
        case 'rescheduled_by_mediator':
          textColor = outlined ? 'text-red-500' : 'text-red-500';
          border = outlined ? 'border border-red-500' : 'border border-red-500';
          bgColor = outlined ? 'bg-white' : 'bg-red-100';
          iconName = 'RescheduleIcon';
          label = 'Reporté';
          break;
        case 'rescheduled_by_applicant':
          textColor = outlined ? 'text-orange-600' : 'text-white';
          border = outlined ? 'border border-orange-500' : '';
          bgColor = outlined ? 'bg-white' : 'bg-orange-400';
          iconName = 'RescheduleIcon';
          label = 'En attente';
          break;
        default:
          textColor = outlined ? 'text-stone-400' : 'text-stone-400';
          border = outlined ? 'border border-dark' : 'border border-stone-300';
          bgColor = outlined ? 'bg-white' : 'bg-stone-50';
          iconName = 'TrashIcon';
          label = 'Evènement inconnu';
          break;
      }
    }

    return (
      <span
        className={`inline-flex items-center px-3 py-2 rounded-sm text-md font-bold ${textColor} ${border} ${bgColor}`}
      >
        <Icon name={iconName} className="w-6 h-6" />
        {label}
      </span>
    );
  }, [event, outlined]);

  return statusBadge;
};

export default EventStatusBadge;
